import "src/public-path";

import React from "react";
import ReactDOM from "react-dom";
import {
  breadboxAPI,
  cached,
  initDatasetRegistry,
  LegacyPortalApiResponse,
} from "@depmap/api";
import { CustomList } from "@depmap/cell-line-selector";

import { getQueryParams, sortByNumberOrNull } from "@depmap/utils";

import ErrorBoundary from "src/common/components/ErrorBoundary";
import { WideTableProps } from "@depmap/wide-table";

import { DataExplorerContextV2, DatasetOption } from "@depmap/types";

import { EntityType } from "./entity/models/entities";
import TermsAndConditionsModal from "./common/components/TermsAndConditionsModal";
import { initializeDevContexts } from "@depmap/data-explorer-2";
import { EnrichmentTile } from "./contextExplorer/components/EnrichmentTile";
import { HeatmapTileContainer } from "./compound/tiles/HeatmapTile/HeatmapTileContainer";
import { StructureAndDetailTile } from "./compound/tiles/StructureAndDetailTile";
import {
  fetchCompoundDatasets,
  getHighestPriorityCompoundDataset,
  getHighestPriorityCorrelationDatasetForEntity,
} from "./compound/utils";
import TopCoDependenciesTile from "./genePage/tiles/TopCoDependencies";
import { SensitivityTile } from "./compound/tiles/SensitivityTile/SensitivityTile";
import { getTopCodependencyDatasetIds } from "./genePage/utils";
import { PredictabilityTile } from "./compound/tiles/PredictabilityTile/PredictabilityTile";
import { DatasetAvailabilityTile } from "./compound/tiles/DataAvailabilityTile/DataAvailabilityTile";

export { log, tailLog, getLogCount } from "src/common/utilities/log";

type EntitySummaryResponse = LegacyPortalApiResponse["getEntitySummary"];

if (["dev.cds.team", "127.0.0.1:5000"].includes(window.location.host)) {
  initializeDevContexts();
}

// Seed the persistent-cache dataset registry. Called synchronously (with the
// pending promises, not the resolved values) so requests issued during these
// round trips wait for it instead of refusing to persist. Never blocks first
// paint, and never rejects — the engine degrades to in-memory caching on any
// failure.
//
// getDatasets goes through cached() so the seed SHARES one request (and one
// listing) with every app caller. That is safe only while getDatasets never
// gains a persist option: a persisted call awaits the registry this promise
// seeds, which would deadlock. ADR 0008 forbids persisting it independently.
initDatasetRegistry({
  datasets: cached(breadboxAPI).getDatasets(),
  breadboxVersion: breadboxAPI.getBreadboxVersion(),
});

const CorrelatedDependenciesTile = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CorrelatedDependenciesTile" */
      "./compound/tiles/CorrelatedTiles/CorrelatedDependenciesTile"
    )
);

const CorrelatedExpressionTile = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CorrelatedExpressionTile" */
      "./compound/tiles/CorrelatedTiles/CorrelatedExpressionTile"
    )
);

const RelatedCompoundsTile = React.lazy(
  () =>
    import(
      /* webpackChunkName: "RelatedCompoundsTile" */
      "./compound/tiles/RelatedCompoundsTile/RelatedCompoundsTile"
    )
);

const DoseCurvesTab = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DoseCurvesTab" */
      "src/compound/doseCurvesTab/DoseCurvesTab"
    )
);

const HeatmapTab = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HeatmapTab" */
      "src/compound/heatmapTab/HeatmapTab"
    )
);

const EntitySummary = React.lazy(
  () =>
    import(
      /* webpackChunkName: "EntitySummary" */
      "src/entity/components/EntitySummary"
    )
);

const PredictabilityTab = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PredictabilityTab" */
      "src/predictability/components/PredictabilityTab"
    )
);

const SublineagePlot = React.lazy(
  () =>
    import(
      /* webpackChunkName: "SublineagePlot" */
      "src/entity/components/SublineagePlot"
    )
);

const WideTable = React.lazy(
  () =>
    import(
      /* webpackChunkName: "WideTable" */
      "@depmap/wide-table"
    )
);

const PortalContextManager = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PortalContextManager" */
      "src/data-explorer-2/components/PortalContextManager"
    )
);

const StandaloneContextEditor = React.lazy(
  () =>
    import(
      /* webpackChunkName: "StandaloneContextEditor" */
      "src/data-explorer-2/components/StandaloneContextEditor"
    )
);

// Render element inside an ErrorBoundary
const renderWithErrorBoundary = (
  element: React.ReactElement,
  container: Element
) => {
  ReactDOM.render(<ErrorBoundary>{element}</ErrorBoundary>, container);
};

export function showTermsAndConditionsModal() {
  const container = document.getElementById("modal-container");
  ReactDOM.render(<TermsAndConditionsModal />, container);
}

export function launchContextManagerModal(options?: {
  initialContextType: string;
  showHelpText: boolean;
}) {
  const container = document.getElementById("modal-container");

  const hide = () => ReactDOM.unmountComponentAtNode(container as HTMLElement);

  // Unmount a previous instance if any (otherwise this is a no-op).
  hide();

  ReactDOM.render(
    <React.Suspense fallback={null}>
      <PortalContextManager
        onHide={hide}
        initialContextType={options?.initialContextType}
        showHelpText={options?.showHelpText || false}
      />
    </React.Suspense>,
    container
  );
}

export function editContext(context: DataExplorerContextV2, hash: string) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const unmount = () => {
    ReactDOM.unmountComponentAtNode(container as HTMLElement);
    container.remove();
  };

  ReactDOM.render(
    <React.Suspense fallback={null}>
      <StandaloneContextEditor context={context} hash={hash} onHide={unmount} />
    </React.Suspense>,
    container
  );
}

export function repairContext(
  context: DataExplorerContextV2,
  startInTableView?: boolean
): Promise<DataExplorerContextV2 | null> {
  const container = document.getElementById("modal-container");

  const unmount = () => {
    ReactDOM.unmountComponentAtNode(container as HTMLElement);
  };

  unmount();

  let resolve: (repairedContext: DataExplorerContextV2 | null) => void;

  const promise = new Promise<DataExplorerContextV2 | null>((origResolve) => {
    resolve = origResolve;
  });

  ReactDOM.render(
    <React.Suspense fallback={null}>
      <StandaloneContextEditor
        context={context}
        hash={null}
        startInTableView={startInTableView}
        onSave={(nextContext: DataExplorerContextV2) => {
          resolve(nextContext);
        }}
        onHide={() => {
          resolve(null);
          unmount();
        }}
      />
    </React.Suspense>,
    container
  );

  return promise;
}

export function saveNewContext(
  context:
    | DataExplorerContextV2
    | {
        dimension_type: string;
        name?: never;
        expr?: never;
        vars?: never;
        contexts?: never;
      },
  onHide?: null | (() => void),
  onSave?: null | ((context: DataExplorerContextV2, hash: string) => void),
  startInTableView?: boolean
) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const unmount = () => {
    ReactDOM.unmountComponentAtNode(container as HTMLElement);
    container.remove();
  };

  ReactDOM.render(
    <React.Suspense fallback={null}>
      <StandaloneContextEditor
        context={context}
        hash={null}
        startInTableView={startInTableView}
        onSave={onSave || undefined}
        onHide={() => {
          if (onHide) {
            onHide();
          }

          unmount();
        }}
      />
    </React.Suspense>,
    container
  );
}

export function launchCellLineSelectorModal() {
  saveNewContext(
    ({
      name: "",
      dimension_type: "depmap_model",
      expr: null,
      // These slices are unreferenced so they will be normalized away on save.
      // They are just here to add more info to the table (which automatically
      // creates columns for all variables).
      vars: {
        oncotree1: {
          dataset_id: "depmap_model_metadata",
          identifier_type: "column",
          identifier: "OncotreeLineage",
        },
        oncotree2: {
          dataset_id: "depmap_model_metadata",
          identifier_type: "column",
          identifier: "OncotreeSubtype",
        },
      },
    } as unknown) as DataExplorerContextV2,
    null,
    null,
    true
  );
}

export function initEnrichmentTile(
  elementId: string,
  featureLabel: string,
  featureType: string
) {
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <EnrichmentTile featureLabel={featureLabel} featureType={featureType} />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export async function initSensitivityTile(
  elementId: string,
  compoundId: string
) {
  const highestPriorityDataset = await getHighestPriorityCompoundDataset(
    compoundId
  );

  if (highestPriorityDataset === null) {
    return;
  }
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <SensitivityTile
        compoundId={compoundId}
        dataset={highestPriorityDataset}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export async function initPredictabilityTile(
  elementId: string,
  compoundId: string
) {
  const highestPriorityDataset = await getHighestPriorityCompoundDataset(
    compoundId
  );

  if (highestPriorityDataset === null) {
    return;
  }
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <PredictabilityTile
        compoundId={compoundId}
        datasetGivenIds={[highestPriorityDataset.given_id!]}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export async function initTopCoDependenciesTile(
  elementId: string,
  entrezId: string,
  geneLabel: string
) {
  const dependencyDatasetIds = await getTopCodependencyDatasetIds(entrezId);

  // Don't show the tile if the gene is not in the RNAi or CRISPR dataset
  if (dependencyDatasetIds.length === 0) {
    return;
  }

  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <TopCoDependenciesTile
        geneEntrezId={entrezId}
        geneLabel={geneLabel}
        associationDatasetIds={dependencyDatasetIds}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export function initHeatmapTile(
  elementId: string,
  compoundId: string,
  compoundName: string
) {
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <HeatmapTileContainer
        compoundId={compoundId}
        compoundName={compoundName}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export async function initCorrelatedExpressionTile(
  elementId: string,
  compoundName: string,
  compoundID: string
): Promise<void> {
  const highestPriorityGivenId = await getHighestPriorityCorrelationDatasetForEntity(
    compoundID
  );

  if (highestPriorityGivenId === null) {
    return;
  }

  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <CorrelatedExpressionTile
        entityLabel={compoundName}
        datasetID={highestPriorityGivenId!}
        associationDatasetId={"expression"}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export async function initCorrelatedDependenciesTile(
  elementId: string,
  compoundName: string,
  compoundID: string
): Promise<void> {
  const highestPriorityGivenId = await getHighestPriorityCorrelationDatasetForEntity(
    compoundID
  );

  if (highestPriorityGivenId === null) {
    return;
  }

  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <CorrelatedDependenciesTile
        entityLabel={compoundName}
        datasetID={highestPriorityGivenId!}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export function initStructureAndDetailTile(
  elementId: string,
  compoundId: string
) {
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <StructureAndDetailTile compoundId={compoundId} />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export async function initRelatedCompoundsTile(
  elementId: string,
  entityLabel: string,
  compoundID: string
): Promise<void> {
  const datasetToDataTypeMap: Record<string, "CRISPR" | "RNAi"> = {
    Chronos_Combined: "CRISPR",
    RNAi_merged: "RNAi",
  };

  const highestPriorityGivenId = await getHighestPriorityCorrelationDatasetForEntity(
    compoundID
  );

  if (highestPriorityGivenId === null) {
    return;
  }

  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <RelatedCompoundsTile
        entityLabel={entityLabel}
        compoundId={compoundID}
        datasetId={highestPriorityGivenId}
        datasetToDataTypeMap={datasetToDataTypeMap}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export async function initDatasetAvailabilityTile(
  elementId: string,
  compoundId: string,
  compoundName: string
) {
  const datasets = await fetchCompoundDatasets(compoundId);
  if (datasets.length === 0) {
    return;
  }
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <DatasetAvailabilityTile
        compoundName={compoundName}
        compoundId={compoundId}
        datasets={datasets}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export function initPredictiveTab(
  elementId: string,
  entityId: number,
  entityLabel: string,
  entityType: EntityType,
  customDownloadsLink: string,
  methodologyUrl: string
) {
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <PredictabilityTab
        entityIdOrLabel={entityId}
        entityLabel={entityLabel}
        entityType={entityType}
        customDownloadsLink={customDownloadsLink}
        methodologyUrl={methodologyUrl}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

// New dose curves tab
export function initDoseCurvesTab(
  elementId: string,
  name: string,
  compoundId: string,
  datasetOptions: Array<any>,
  units: string
) {
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <DoseCurvesTab
        datasetOptions={sortByNumberOrNull(
          datasetOptions,
          "auc_dataset_priority",
          "asc"
        )}
        doseUnits={units}
        compoundName={name}
        compoundId={compoundId}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export function initHeatmapTab(
  elementId: string,
  name: string,
  compoundId: string,
  datasetOptions: Array<any>,
  units: string
) {
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <HeatmapTab
        datasetOptions={sortByNumberOrNull(
          datasetOptions,
          "auc_dataset_priority",
          "asc"
        )}
        doseUnits={units}
        compoundName={name}
        compoundId={compoundId}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export function initWideTable(elementId: string, config: WideTableProps) {
  const {
    data,
    columns,
    invisibleColumns,
    defaultColumnsToShow,
    columnOrdering,
    additionalReactTableProps,
    downloadURL,
    allowDownloadFromTableData,
    sorted, // TODO: Actually used?
    renderExtraDownloads,
  } = config;

  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <WideTable
        data={data}
        columns={columns}
        invisibleColumns={invisibleColumns}
        defaultColumnsToShow={defaultColumnsToShow}
        columnOrdering={columnOrdering}
        additionalReactTableProps={additionalReactTableProps}
        downloadURL={downloadURL}
        allowDownloadFromTableData={allowDownloadFromTableData}
        sorted={sorted}
        renderExtraDownloads={renderExtraDownloads}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

export function initEntitySummary(
  elementId: string,
  summary: {
    initialSelectedDataset: DatasetOption;
    size_biom_enum_name: string;
    color: string;
    figure: { name: number };
    show_auc_message: boolean;
    summary_options: Array<DatasetOption>;
  }
) {
  const {
    size_biom_enum_name: sizeBiomEnumName,
    color,
    figure,
    show_auc_message: showAUCMessage,
    summary_options: summaryOptions,
  } = summary;
  const query = getQueryParams();
  let initialSelectedDataset = summary.summary_options[0];

  if ("dependency" in query) {
    const queryDataset = summary.summary_options.find(
      (o) => o.dataset === query.dependency
    );

    if (queryDataset) {
      initialSelectedDataset = queryDataset;
    }
  }
  renderWithErrorBoundary(
    <React.Suspense fallback={<div>Loading...</div>}>
      <EntitySummary
        size_biom_enum_name={sizeBiomEnumName}
        color={color}
        figure={figure}
        show_auc_message={showAUCMessage}
        summary_options={summaryOptions}
        initialSelectedDataset={initialSelectedDataset}
      />
    </React.Suspense>,
    document.getElementById(elementId) as HTMLElement
  );
}

// fixme: take this out when characterization moves to the full entitySummary
export function initSublineagePlot(
  elementId: string,
  datasetEntitySummary: EntitySummaryResponse,
  cellLineList: CustomList | undefined,
  name: number,
  showSublineageCheckboxId: string,
  rerenderPlotEventName: string
) {
  const attachEventListenerForPlotShown = (handlePlotShown: () => void) => {
    $('a[href="#characterization"]').on("shown.bs.tab", () => {
      handlePlotShown();
    });
    $(".characterizationRadio").change(() => {
      handlePlotShown();
    });
  };

  const removeEventListenerForPlotShown = () => {
    $('a[href="#dependency"]').off("shown.bs.tab");
  };

  const cellLinesToHighlight =
    (cellLineList?.lines as Set<string>) || new Set();

  const renderPlot = () => {
    const showSublineageCheckbox = document.getElementById(
      showSublineageCheckboxId
    ) as HTMLInputElement;
    const showSublineage = showSublineageCheckbox.checked;
    renderWithErrorBoundary(
      <React.Suspense fallback={<div>Loading...</div>}>
        <SublineagePlot
          datasetEntitySummary={datasetEntitySummary}
          elementId={`sublineage_plot_${name}`}
          attachEventListenerForPlotShown={attachEventListenerForPlotShown}
          removeEventListenerForPlotShown={removeEventListenerForPlotShown}
          showSublineage={showSublineage}
          cellLinesToHighlight={cellLinesToHighlight}
          key={[...cellLinesToHighlight].toString()}
        />
      </React.Suspense>,
      document.getElementById(elementId) as HTMLElement
    );
  };

  // hook only for the characterization tab, so that the plot is redrawn when show sublineage changes
  window.addEventListener(rerenderPlotEventName, renderPlot);

  renderPlot();
}
